import { Button } from "../../../components/ui";
import Modal from "../../../components/base/Modal";
import type { ShipmentEvent } from "../../../features/shipments/types";

interface EventsDetailModalProps {
  isOpen: boolean;
  events: ShipmentEvent[];
  onClose: () => void;
}

export function EventsDetailModal({ isOpen, events, onClose }: EventsDetailModalProps) {
  console.log("📋 EventsDetailModal rendered - isOpen:", isOpen, "events:", events.length);

  if (!isOpen) {
    console.log("📋 Modal is closed, returning null");
    return null;
  }

  console.log("📋 Modal is open, rendering content");

  const isImageFile = (url: string): boolean => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
    return imageExtensions.some((ext) => url.toLowerCase().includes(ext));
  };

  const formatEventDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("es-MX", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  };

  const getFileName = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      return decodeURIComponent(pathname.split("/").pop() || "archivo");
    } catch {
      return "archivo";
    }
  };

  const handleDownload = (url: string) => {
    const fileName = getFileName(url);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const sortedEvents = [...events].reverse();

  const content = (
    <div className="space-y-4">
      {sortedEvents.length > 0 ? (
        sortedEvents.map((event, index) => (
          <div
            key={event.id}
            className="rounded-lg border border-line p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-500/10 text-xs font-semibold text-primary-500">
                    {index + 1}
                  </span>
                  <h4 className="font-semibold text-content">
                    {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                  </h4>
                </div>
                <p className="mt-1 text-xs text-content-muted">
                  {formatEventDate(event.date)}
                </p>
              </div>
            </div>

            <div className="space-y-2 border-t border-line pt-3">
              {event.address && (
                <div>
                  <p className="text-xs font-medium text-content-muted">Ubicación:</p>
                  <p className="text-sm text-content">{event.address}</p>
                </div>
              )}

              {event.lat !== null && event.long !== null && (
                <div>
                  <p className="text-xs font-medium text-content-muted">Coordenadas:</p>
                  <p className="text-sm font-mono text-content">
                    {event.lat.toFixed(6)}, {event.long.toFixed(6)}
                  </p>
                </div>
              )}

              {event.note && (
                <div>
                  <p className="text-xs font-medium text-content-muted">Nota:</p>
                  <p className="text-sm text-content">{event.note}</p>
                </div>
              )}
            </div>

            {event.file_urls && event.file_urls.length > 0 && (
              <div className="border-t border-line pt-3">
                <p className="mb-2 text-xs font-medium text-content-muted">
                  Archivos ({event.file_urls.length}):
                </p>
                <div className="space-y-2">
                  {event.file_urls.map((fileUrl, fileIndex) => {
                    const fileName = getFileName(fileUrl);
                    const isImage = isImageFile(fileUrl);

                    return (
                      <div
                        key={`${event.id}-file-${fileIndex}`}
                        className="rounded-lg bg-subtle p-3"
                      >
                        {isImage ? (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-content truncate">
                              {fileName}
                            </p>
                            <div className="relative overflow-hidden rounded-lg bg-card">
                              <img
                                src={fileUrl}
                                alt={fileName}
                                className="max-h-48 w-full object-contain"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <i className="ri-file-line text-lg text-content-muted shrink-0" />
                              <span className="text-xs font-medium text-content truncate">
                                {fileName}
                              </span>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<i className="ri-download-line" />}
                              onClick={() => handleDownload(fileUrl)}
                              title="Descargar archivo"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))
      ) : (
        <p className="text-center text-sm text-content-muted">Sin eventos registrados</p>
      )}
    </div>
  );

  return (
    <Modal
      title="Historial de eventos detallado"
      content={content}
      onClose={onClose}
      width="max-w-3xl"
    />
  );
}
